import { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "mailwave-theme";

const ThemeContext = createContext(null);

function getSystemTheme() {
  if (typeof window === "undefined") return "light";
  return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
}

function resolveTheme(theme) {
  if (theme === "system") return getSystemTheme();
  return theme === "dark" ? "dark" : "light";
}

function applyTheme(resolved) {
  const root = document.documentElement;
  root.classList.toggle("dark", resolved === "dark");
  root.style.colorScheme = resolved;
}

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) || "system";
    } catch {
      return "system";
    }
  });

  const resolved = useMemo(() => resolveTheme(theme), [theme]);

  useEffect(() => {
    applyTheme(resolved);
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      /* ignore */
    }
  }, [theme, resolved]);

  useEffect(() => {
    if (theme !== "system") return undefined;
    const mq = window.matchMedia("(prefers-color-scheme: dark)");
    const onChange = () => applyTheme(resolveTheme("system"));
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, [theme]);

  const value = useMemo(
    () => ({
      theme,
      resolved,
      setTheme,
      toggleTheme: () => setTheme((t) => (resolveTheme(t) === "dark" ? "light" : "dark")),
    }),
    [theme, resolved],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error("useTheme must be used within ThemeProvider");
  return ctx;
}

/** Apply saved theme before React paints (call once in index.js). */
export function initTheme() {
  try {
    const stored = localStorage.getItem(STORAGE_KEY) || "system";
    applyTheme(resolveTheme(stored));
  } catch {
    applyTheme("light");
  }
}
